import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { isEngagementDetailsPath } from '../../../util/routeUtils'

type UseClearSelectionOnChannelChangeParams = {
  channelIds: string[]
  onChannelSelectionChange: () => void
}

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index])

/**
 * Clears the selected post when the channel selection changes, so the detail
 * pane never keeps showing a post from a channel that left the scope.
 *
 * Two changes are not a selection change and are ignored:
 * - The list filling in from empty. The channel list reads as empty until both
 *   the channels and the configuration queries have resolved, so a fresh page
 *   load goes from no channels to every channel without the user touching the
 *   filter.
 *
 * A change while an engagement-details dialog (comment or mention) is open
 * over the view is held until the dialog closes: clearing navigates to the
 * posts route, which would unmount the dialog route out from under the actor.
 */
export function useClearSelectionOnChannelChange({
  channelIds,
  onChannelSelectionChange,
}: UseClearSelectionOnChannelChangeParams): void {
  const location = useLocation()
  const isDetailsDialogOpen = isEngagementDetailsPath(location.pathname)
  const previousChannelIdsRef = useRef(channelIds)

  useEffect(() => {
    if (isDetailsDialogOpen) return

    const previousIds = previousChannelIdsRef.current
    previousChannelIdsRef.current = channelIds

    if (sameIds(previousIds, channelIds)) return
    if (previousIds.length === 0) return

    onChannelSelectionChange()
  }, [channelIds, onChannelSelectionChange, isDetailsDialogOpen])
}
